import { REQUIRED_LIGHTING } from "@/features/presets/params"

/** Options to configure LIQA‘s look and behavior. */
export type Props = {
  /** The license key obtained from {@link https://haut.ai/#contacts Haut.ai }.  */
  license: string
  /** A string containing valid CSS rules. */
  styles?: string
  /** An object whose keys are translation keys and values are translation texts. */
  messages?: Messages
  /**
   * A preset used to customize AI quality criteria and user flow.
   * @default "face"
   */
  preset?: "face" | "face-180" | "hair"
  /**
   * Overrides for the default preset‘s quality settings.
   * @hidden
   * @deprecated Use `requiredLighting`.
   */
  settings?: Partial<{ lighting: number }>
  /**
   * A list of sources to capture the image from.
   *
   * If "front_camera" is specified, the front camera will be used to capture the image.
   * If the device has no front camera but has a back camera - the back camera will be used as a fallback.
   *
   * If "back_camera" is specified, the back camera will be used to capture the image.
   * If the device has no back camera but has a front camera - the front camera will be used as a fallback.
   *
   * If "camera" is specified - acts as an alias for "front_camera".
   *
   * If more than one camera is specified as a source, the first one will be used as the initial one.
   *
   * If only one "upload" source is specified, the file upload will be used to capture the image.
   * @default ["camera"]
   */
  sources?:
    | ["camera" | "front_camera" | "back_camera"]
    | ["upload"]
    | ["camera" | "front_camera" | "back_camera", "upload"]
  /**
   * Image capture mode for `"camera"` source.
   * `"auto"` means LIQA will automatically take a photo when all AI quality criteria met.
   * `"manual"` means LIQA will allow the user to take a photo on their own via a button.
   * The option is only applicable to "hair" preset. For other presets it is always "auto".
   */
  capture?: "auto" | "manual"
  /**
   * Lighting requirements for the image capture.
   * - `"none"`: no requirements are set and lighting will not be validated. (**WARNING**: the quality of the processing results may be affected)
   * - `"adaptive"`: the requirements will adapt to the lighting conditions.
   * - `"default"`: the minimum requirements will be set to optimal values which will provide more details on the photo.
   *
   * Default value: `"default"`
   */
  requiredLighting?: REQUIRED_LIGHTING
}

export type Messages = {
  [key: string]: string | Messages
}

/** Preset to be used if no `preset` option is specified to LIQA */
export const DEFAULT_PRESET = "face"

const ALLOWED_SOURCES_BY_PRESET = {
  // face-180 has no upload and back_camera options
  "face-180": ["front_camera", "camera"],
} satisfies Partial<Record<NonNullable<Props["preset"]>, NonNullable<Props["sources"]>[number][]>>
const DEFAULT_SOURCES_BY_PRESET: Record<
  keyof typeof ALLOWED_SOURCES_BY_PRESET,
  NonNullable<Props["sources"]>[number][]
> = {
  "face-180": ["front_camera"],
}

const shouldValidateSources = (
  preset: Props["preset"],
): preset is keyof typeof ALLOWED_SOURCES_BY_PRESET =>
  Boolean(preset && preset in ALLOWED_SOURCES_BY_PRESET)

export const adjustPresetSources = <T extends string[]>(
  sources: T,
  { preset, showWarning }: { preset: Props["preset"]; showWarning?: boolean },
) => {
  if (!shouldValidateSources(preset)) {
    return sources
  }

  const allowedSources = ALLOWED_SOURCES_BY_PRESET[preset] as T
  const adjustedSources = sources.filter((source) => {
    const isAllowedSource = allowedSources.includes(source)

    if (!isAllowedSource && showWarning) {
      console.warn(
        `[LIQA] Warning: the "${source}" source is not supported for the "${preset}" preset.`,
      )
    }

    return isAllowedSource
  }) as T

  if (adjustedSources.length > 0) {
    return adjustedSources
  }

  const defaultSources = DEFAULT_SOURCES_BY_PRESET[preset] ?? []

  if (showWarning) {
    console.warn(
      `[LIQA] Warning: No valid sources were provided for the selected preset ("${preset}"). ` +
        (defaultSources.length > 0
          ? `Defaulting to [${defaultSources.map((s) => `"${s}"`).join(", ")}]. `
          : "") +
        "Please check the presets and image sources documentation and make sure you are using the correct sources. https://docs.liqa.haut.ai/customization/user-flow/image-sources.html",
    )
  }

  return defaultSources
}

/** Converts old sources to modern providing backward compatibility and warns of deprecated props. */
export const oldSourcesToNewSources = <T extends string[]>(sources: T) => {
  if (sources.includes("photo")) {
    console.warn(
      `[LIQA] Deprecation warning: the "photo" option of the "sources" API has been deprecated. Use "upload" instead.`,
    )
    return sources.map((source) => (source === "photo" ? "upload" : source)) as T
  }
  return sources
}

export function settingsToRequiredLighting(settings?: Props["settings"]) {
  if (settings)
    console.warn(
      '[LIQA] Warning: "settings" attribute is deprecated. Use "requiredLighting" instead.',
    )

  return settings?.lighting === 0 ? REQUIRED_LIGHTING.NONE : undefined
}
